import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { formatArgentinaDate } from './dateUtils';
import { Invoice, Payment, Budget, BudgetItem } from '../types';
import { formatCurrency } from './formatters';
import { supabase } from '../lib/supabase';
import { loadImageForPdf } from './budgetAttachments';

// Company info
const COMPANY_NAME = 'PRODUCTEX';
const COMPANY_LEGAL_NAME = 'RGX GROUP SRL';
const COMPANY_ADDRESS = 'Nuñez 1567, Ciudad Autónoma de Buenos Aires';
const COMPANY_EMAIL = 'hola@productex.com.ar';
const COMPANY_PHONE = '+5491154731466';
const COMPANY_CUIT = '30-71892824-5';

// Función para convertir números a palabras en español
const numeroALetras = (numero: number): string => {
  const unidades = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
  const decenas = ['', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
  const especiales = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
  const centenas = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

  if (numero === 0) return 'cero';
  if (numero < 0) return 'menos ' + numeroALetras(Math.abs(numero));

  let letras = '';

  // Millones
  if (numero >= 1000000) {
    const millones = Math.floor(numero / 1000000);
    numero %= 1000000;
    if (millones === 1) {
      letras += 'un millón ';
    } else {
      letras += numeroALetras(millones) + ' millones ';
    }
  }

  // Miles
  if (numero >= 1000) {
    const miles = Math.floor(numero / 1000);
    numero %= 1000;
    if (miles === 1) {
      letras += 'mil ';
    } else {
      letras += numeroALetras(miles) + ' mil ';
    }
  }

  // Centenas
  if (numero >= 100) {
    if (numero === 100) {
      letras += 'cien ';
    } else {
      letras += centenas[Math.floor(numero / 100)] + ' ';
    }
    numero %= 100;
  }

  // Decenas y unidades
  if (numero > 0) {
    if (numero < 10) {
      letras += unidades[numero];
    } else if (numero < 20) {
      letras += especiales[numero - 10];
    } else {
      const unidad = numero % 10;
      const decena = Math.floor(numero / 10);
      if (unidad === 0) {
        letras += decenas[decena];
      } else {
        letras += decenas[decena] + ' y ' + unidades[unidad];
      }
    }
  }

  return letras.trim();
};

export const generateInvoicePDF = async (invoice: Invoice, entityName: string) => {
  // Obtener items de la factura si no los tiene
  let invoiceItems = invoice.items;
  if (!invoiceItems || invoiceItems.length === 0) {
    try {
      const { data, error } = await supabase
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching invoice items:', error);
        invoiceItems = [];
      } else {
        invoiceItems = data || [];
      }
    } catch (error) {
      console.error('Error fetching invoice items:', error);
      invoiceItems = [];
    }
  }

  // Generar siempre el PDF de factura, sin omitir por tipo o entidad
  const doc = new jsPDF();
  
  // Set font
  doc.setFont('helvetica');
  
  // Company header - Left side
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_NAME, 10, 20);
  
  // Document type below company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`FACTURA ${invoice.type || 'X'}`, 10, 30);
  
  // Company details - Right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, doc.internal.pageSize.width - 10, 15, { align: 'right' });
  doc.text(COMPANY_ADDRESS, doc.internal.pageSize.width - 10, 20, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, doc.internal.pageSize.width - 10, 25, { align: 'right' });
  doc.text(`E-mail: ${COMPANY_EMAIL}`, doc.internal.pageSize.width - 10, 30, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, doc.internal.pageSize.width - 10, 35, { align: 'right' });
  
  // Document validity notice - Center
  doc.setFontSize(10);
  doc.setFont('helvetica');
  doc.text('DOCUMENTO NO VÁLIDO COMO FACTURA', doc.internal.pageSize.width / 2, 45, { align: 'center' });
  
  // Draw horizontal line
  doc.line(10, 55, doc.internal.pageSize.width - 10, 55);
  
  // Client and Invoice details in a table-like structure
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Create a table with two columns
  const startY = 65;
  
  // Calculate client data height dynamically
  let clientHeight = 40; // Base height
  
  // Left column - Client data
  doc.text('Datos del Cliente', 15, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  // Get client details from the invoice if available
  const client = invoice.entity_id ? await getClientDetails(invoice.entity_id) : null;
  
  if (client) {
    let yPos = startY + 20;
    
    // Nombre del cliente
    if (client.name) {
      doc.text(`Nombre: ${client.name}`, 15, yPos);
      yPos += 5;
    }
    
    // Razón social
    if (client.business_name) {
      doc.text(`Razón Social: ${client.business_name}`, 15, yPos);
      yPos += 5;
    }
    
    // Dirección
    if (client.address) {
      doc.text(`Dirección: ${client.address}`, 15, yPos);
      yPos += 5;
    }
    
    // Ciudad y Provincia
    if (client.city || client.province) {
      const location = [client.city, client.province].filter(Boolean).join(', ');
      if (location) {
        doc.text(`Ciudad/Provincia: ${location}`, 15, yPos);
        yPos += 5;
      }
    }
    
    // Código postal
    if (client.postal_code) {
      doc.text(`CP: ${client.postal_code}`, 15, yPos);
      yPos += 5;
    }
    
    // CUIT
    if (client.cuit) {
      doc.text(`CUIT: ${client.cuit}`, 15, yPos);
      yPos += 5;
    }
    
    // Condición fiscal
    if (client.tax_condition) {
      const taxConditionMap: Record<string, string> = {
        'RI': 'Responsable Inscripto',
        'MT': 'Monotributista',
        'EX': 'Exento',
        'CF': 'Consumidor Final',
        'NC': 'No Categorizado'
      };
      
      doc.text(`IVA: ${taxConditionMap[client.tax_condition] || client.tax_condition}`, 15, yPos);
      yPos += 5;
    }
    
    // Teléfono
    if (client.phone) {
      doc.text(`Teléfono: ${client.phone}`, 15, yPos);
      yPos += 5;
    }
    
    // Email
    if (client.email) {
      doc.text(`Email: ${client.email}`, 15, yPos);
      yPos += 5;
    }
    
    // (Oculto) Vendedor y Saldo: no se muestran en exportaciones
    
    // Observaciones
    if (client.notes) {
      doc.text(`Observaciones: ${client.notes}`, 15, yPos);
      yPos += 5;
    }

    // Calculate actual height needed for client data
    clientHeight = Math.max(clientHeight, yPos - startY + 10);
  } else {
    doc.text(entityName, 15, startY + 20);
  }
  
  // Draw rectangle for client data with calculated height
  doc.rect(10, startY, doc.internal.pageSize.width / 2 - 15, clientHeight);
  
  // Draw rectangle for invoice data with same height as client data
  doc.rect(doc.internal.pageSize.width / 2 - 5, startY, doc.internal.pageSize.width / 2 - 5, clientHeight);
  
  // Right column - Invoice data
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Factura', doc.internal.pageSize.width / 2, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nro.: ${invoice.number}`, doc.internal.pageSize.width / 2, startY + 20);
  doc.text(`Fecha: ${formatArgentinaDate(invoice.date)}`, doc.internal.pageSize.width / 2, startY + 30);
  
  // Add due date if available
  if (invoice.due_date) {
    doc.text(`Vencimiento: ${formatArgentinaDate(invoice.due_date)}`, doc.internal.pageSize.width / 2, startY + 40);
  }
  
  // Items table
  doc.autoTable({
    startY: startY + clientHeight + 10,
    head: [[
      'DESCRIPCIÓN',
      'CANTIDAD',
      'VALOR UNITARIO',
      invoice.type === 'X' ? 'RECARGO' : 'IVA',
      'TOTAL'
    ]],
    body: invoiceItems.map(item => [
      item.description,
      item.quantity.toString(),
      formatCurrency(item.unit_price),
      `${item.tax_rate}%`,
      formatCurrency(item.total),
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 25, halign: 'right' }, // Increased width for CANTIDAD
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 26, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
    },
    theme: 'plain',
    margin: { left: 10, right: 10 },
  });
  
  // Totals
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  
  // Add separator line before totals
  doc.setDrawColor(0);
  doc.line(10, finalY - 5, doc.internal.pageSize.width - 10, finalY - 5);
  
  // Right-aligned totals
  const rightMargin = doc.internal.pageSize.width - 10;
  
  doc.setFontSize(9);
  doc.text('SUBTOTAL', rightMargin - 80, finalY);
  doc.text(formatCurrency(invoice.subtotal), rightMargin, finalY, { align: 'right' });
  
  doc.text(invoice.type === 'X' ? 'RECARGO' : 'IVA', rightMargin - 80, finalY + 7);
  doc.text(formatCurrency(invoice.tax), rightMargin, finalY + 7, { align: 'right' });
  
  // Add separator line before total
  doc.setDrawColor(0);
  doc.line(rightMargin - 100, finalY + 12, rightMargin, finalY + 12);
  
  // Total in bold
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', rightMargin - 80, finalY + 20);
  doc.text(formatCurrency(invoice.total), rightMargin, finalY + 20, { align: 'right' });

  // Currency legend if USD
  if (invoice.currency === 'USD') {
    const curY = finalY + 30;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const rate = invoice.exchange_rate || 0;
    doc.text('Moneda: Dólar Estadounidense (USD)', rightMargin - 80, curY);
    doc.text(`TC: ${rate.toFixed ? rate.toFixed(3) : rate} ARS/USD`, rightMargin, curY, { align: 'right' });
    // Total in ARS if available
    if (invoice.total_ars) {
      doc.text('Total (ARS)', rightMargin - 80, curY + 7);
      doc.text(formatCurrency(invoice.total_ars), rightMargin, curY + 7, { align: 'right' });
    }
  }
  
  // Save the PDF
  doc.save(`factura-${invoice.number}.pdf`);
};

export const generateBudgetPDF = async (budget: Budget, entityName: string) => {
  // Verificar si el budget tiene items, si no, obtenerlos de la BD
  let budgetItems = budget.items;
  if (!budgetItems || budgetItems.length === 0) {
    console.log('Budget items not found in object, fetching from database...');
    budgetItems = await getBudgetItems(budget.id);
  } else {
    console.log('Budget items found in object:', budgetItems);
  }

  const doc = new jsPDF();
  
  // Set font
  doc.setFont('helvetica');
  
  // Company header - Left side
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_NAME, 10, 20);
  
  // Document type below company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`PRESUPUESTO ${budget.type || 'X'}`, 10, 30);
  
  // Company details - Right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, doc.internal.pageSize.width - 10, 15, { align: 'right' });
  doc.text(COMPANY_ADDRESS, doc.internal.pageSize.width - 10, 20, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, doc.internal.pageSize.width - 10, 25, { align: 'right' });
  doc.text(`E-mail: ${COMPANY_EMAIL}`, doc.internal.pageSize.width - 10, 30, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, doc.internal.pageSize.width - 10, 35, { align: 'right' });
  
  // Draw horizontal line
  doc.line(10, 55, doc.internal.pageSize.width - 10, 55);
  
  // Client and Budget details in a table-like structure
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Create a table with two columns
  const startY = 65;
  
  // Calculate client data height dynamically
  let clientHeight = 40; // Base height
  
  // Left column - Client data
  doc.text('Datos del Cliente', 15, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  // Get client details from the budget if available
  const client = budget.entity_id ? await getClientDetails(budget.entity_id) : null;
  
  if (client) {
    let yPos = startY + 20;
    doc.text(client.name, 15, yPos);
    yPos += 7;
    
    if (client.address) {
      doc.text(client.address, 15, yPos);
      yPos += 7;
    }
    
    if (client.phone) {
      doc.text(`Tel: ${client.phone}`, 15, yPos);
      yPos += 7;
    }
    
    if (client.email) {
      doc.text(`E-mail: ${client.email}`, 15, yPos);
      yPos += 7;
    }
    
    // Adjust client height based on content
    clientHeight = Math.max(clientHeight, yPos - startY + 10);
  } else {
    doc.text(entityName, 15, startY + 20);
  }
  
  // Draw rectangle for client data with calculated height
  doc.rect(10, startY, doc.internal.pageSize.width / 2 - 15, clientHeight);
  
  // Draw rectangle for budget data with same height as client data
  doc.rect(doc.internal.pageSize.width / 2 - 5, startY, doc.internal.pageSize.width / 2 - 5, clientHeight);
  
  // Right column - Budget data
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Presupuesto', doc.internal.pageSize.width / 2, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nro.: ${budget.number}`, doc.internal.pageSize.width / 2, startY + 20);
  doc.text(`Fecha: ${formatArgentinaDate(budget.date)}`, doc.internal.pageSize.width / 2, startY + 30);
  
  // Add valid until date if available
  if (budget.valid_until) {
    doc.text(`Válido hasta: ${formatArgentinaDate(budget.valid_until)}`, doc.internal.pageSize.width / 2, startY + 40);
  }
  
  // Items table - use the budget items we already obtained
  console.log('Using budget items for PDF generation:', budgetItems);

  const hideTotals = !!budget.hide_pdf_totals;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  const imageWidth = contentWidth * 0.25;

  const tableHead = hideTotals
    ? [['DESCRIPCIÓN', 'CANTIDAD', 'VALOR UNITARIO', budget.type === 'X' ? 'RECARGO' : 'IVA']]
    : [[
        'DESCRIPCIÓN',
        'CANTIDAD',
        'VALOR UNITARIO',
        budget.type === 'X' ? 'RECARGO' : 'IVA',
        'TOTAL',
      ]];

  const columnStyles = hideTotals
    ? {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 25, halign: 'right' as const },
        2: { cellWidth: 35, halign: 'right' as const },
        3: { cellWidth: 30, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 25, halign: 'right' as const },
        2: { cellWidth: 30, halign: 'right' as const },
        3: { cellWidth: 26, halign: 'right' as const },
        4: { cellWidth: 30, halign: 'right' as const },
      };

  const tableStyles = {
    fontSize: 8,
    cellPadding: 3,
  };

  const headStyles = {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [0, 0, 0] as [number, number, number],
    fontStyle: 'bold' as const,
    lineWidth: 0.1,
    lineColor: [0, 0, 0] as [number, number, number],
  };

  let currentY = startY + clientHeight + 10;

  for (let i = 0; i < budgetItems.length; i++) {
    const item = budgetItems[i];
    const row = hideTotals
      ? [
          item.description,
          item.quantity.toString(),
          formatCurrency(item.unit_price),
          `${item.tax_rate}%`,
        ]
      : [
          item.description,
          item.quantity.toString(),
          formatCurrency(item.unit_price),
          `${item.tax_rate}%`,
          formatCurrency(item.total),
        ];

    doc.autoTable({
      startY: currentY,
      head: i === 0 ? tableHead : [],
      body: [row],
      styles: tableStyles,
      headStyles,
      columnStyles,
      theme: 'plain',
      margin: { left: margin, right: margin },
    });

    currentY = (doc as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

    if (item.image_url) {
      try {
        const img = await loadImageForPdf(item.image_url);
        const aspect = img.height / img.width;
        const imageHeight = imageWidth * aspect;

        if (currentY + imageHeight > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }

        doc.addImage(img.data, img.format, margin, currentY, imageWidth, imageHeight);
        currentY += imageHeight + 8;
      } catch (error) {
        console.warn('No se pudo incluir la imagen del ítem en el PDF:', error);
      }
    }
  }

  if (!hideTotals) {
    // Totals
    const finalY = currentY + 6;

    // Add separator line before totals
    doc.setDrawColor(0);
    doc.line(10, finalY - 5, doc.internal.pageSize.width - 10, finalY - 5);

    // Right-aligned totals
    const rightMargin = doc.internal.pageSize.width - 10;

    doc.setFontSize(9);
    doc.text('SUBTOTAL', rightMargin - 80, finalY);
    doc.text(formatCurrency(budget.subtotal), rightMargin, finalY, { align: 'right' });

    doc.text(budget.type === 'X' ? 'RECARGO' : 'IVA', rightMargin - 80, finalY + 7);
    doc.text(formatCurrency(budget.tax), rightMargin, finalY + 7, { align: 'right' });

    // Add separator line before total
    doc.setDrawColor(0);
    doc.line(rightMargin - 100, finalY + 12, rightMargin, finalY + 12);

    // Total in bold
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL', rightMargin - 80, finalY + 20);
    doc.text(formatCurrency(budget.total), rightMargin, finalY + 20, { align: 'right' });
  }
  
  // Save the PDF
  doc.save(`presupuesto-${budget.number}.pdf`);
};

export const generatePaymentPDF = async (payment: Payment, entityName: string) => {
  // Check if this is a supplier payment by looking at the related invoice
  const invoice = payment.invoice_id ? 
    await getInvoiceDetails(payment.invoice_id) : 
    null;
  
  // Skip PDF generation for supplier payments
  if (invoice && invoice.entity_type === 'supplier') {
    console.log('Skipping PDF generation for supplier payment');
    return;
  }
  
  const doc = new jsPDF();
  
  // Company header - Left side
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_NAME, 10, 20);
  
  // Document type below company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('RECIBO', 10, 30);
  
  // Company details - Right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, doc.internal.pageSize.width - 10, 15, { align: 'right' });
  doc.text(COMPANY_ADDRESS, doc.internal.pageSize.width - 10, 20, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, doc.internal.pageSize.width - 10, 25, { align: 'right' });
  doc.text(`e-mail: ${COMPANY_EMAIL}`, doc.internal.pageSize.width - 10, 30, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, doc.internal.pageSize.width - 10, 35, { align: 'right' });
  
  // Document validity notice - Center
  doc.setFontSize(10);
  doc.setFont('helvetica');
  doc.text('DOCUMENTO NO VÁLIDO COMO FACTURA', doc.internal.pageSize.width / 2, 45, { align: 'center' });
  
  // Draw horizontal line
  doc.line(10, 55, doc.internal.pageSize.width - 10, 55);
  
  // Client and Receipt details in a table-like structure
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Create a table with two columns
  const startY = 65;
  
  // Calculate client data height dynamically
  let clientHeight = 40; // Base height
  
  // Left column - Client data
  doc.text('Datos del Cliente', 15, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  // Get client details from the invoice if available, or try to get from entityName
  let client = null;
  if (invoice?.entity_id) {
    client = await getClientDetails(invoice.entity_id);
  } else if (entityName && entityName !== 'Cliente no encontrado') {
    // If no invoice, try to find client by name (this is a fallback)
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .ilike('name', `%${entityName}%`)
        .single();
      
      if (!error && data) {
        client = data;
      }
    } catch (error) {
      console.log('Could not find client by name, using entityName as fallback');
    }
  }
  
  if (client) {
    let yPos = startY + 20;
    
    // Nombre del cliente - solo si tiene valor
    if (client.name && client.name.trim()) {
      doc.text(`Nombre: ${client.name}`, 15, yPos);
      yPos += 5;
    }
    
    // Razón social
    if (client.business_name && client.business_name.trim()) {
      doc.text(`Razón Social: ${client.business_name}`, 15, yPos);
      yPos += 5;
    }
    
    // Dirección - solo si tiene valor
    if (client.address && client.address.trim()) {
      doc.text(`Dirección: ${client.address}`, 15, yPos);
      yPos += 5;
    }
    
    // Ciudad y Provincia
    if (client.city || client.province) {
      const location = [client.city, client.province].filter(Boolean).join(', ');
      if (location && location.trim()) {
        doc.text(`Ciudad/Provincia: ${location}`, 15, yPos);
        yPos += 5;
      }
    }
    
    // Código postal
    if (client.postal_code && client.postal_code.trim()) {
      doc.text(`CP: ${client.postal_code}`, 15, yPos);
      yPos += 5;
    }
    
    // CUIT
    if (client.cuit && client.cuit.trim()) {
      doc.text(`CUIT: ${client.cuit}`, 15, yPos);
      yPos += 5;
    }
    
    // Condición fiscal
    if (client.tax_condition && client.tax_condition.trim()) {
      const taxConditionMap: Record<string, string> = {
        'RI': 'Responsable Inscripto',
        'MT': 'Monotributista',
        'EX': 'Exento',
        'CF': 'Consumidor Final',
        'NC': 'No Categorizado'
      };
      
      doc.text(`IVA: ${taxConditionMap[client.tax_condition] || client.tax_condition}`, 15, yPos);
      yPos += 5;
    }
    
    // Teléfono - solo si tiene valor
    if (client.phone && client.phone.trim()) {
      doc.text(`Teléfono: ${client.phone}`, 15, yPos);
      yPos += 5;
    }
    
    // Email - solo si tiene valor
    if (client.email && client.email.trim()) {
      doc.text(`Email: ${client.email}`, 15, yPos);
      yPos += 5;
    }
    
    // Observaciones
    if (client.notes && client.notes.trim()) {
      doc.text(`Observaciones: ${client.notes}`, 15, yPos);
      yPos += 5;
    }

    // Calculate actual height needed for client data
    clientHeight = Math.max(clientHeight, yPos - startY + 10);
  } else {
    // Si no hay datos del cliente, mostrar al menos el nombre de la entidad
    doc.text(`Cliente: ${entityName || 'No especificado'}`, 15, startY + 20);
    doc.text('No se encontraron datos adicionales del cliente', 15, startY + 25);
  }
  
  // Draw rectangle for client data with calculated height
  doc.rect(10, startY, doc.internal.pageSize.width / 2 - 15, clientHeight);
  
  // Draw rectangle for receipt data with same height as client data
  doc.rect(doc.internal.pageSize.width / 2 - 5, startY, doc.internal.pageSize.width / 2 - 5, clientHeight);
  
  // Right column - Receipt data
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Recibo', doc.internal.pageSize.width / 2, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nro.: ${payment.receipt_number || payment.id.substring(0, 8)}`, doc.internal.pageSize.width / 2, startY + 20);
  doc.text(`Fecha: ${formatArgentinaDate(payment.date)}`, doc.internal.pageSize.width / 2, startY + 30);
  
  // Payment details
  const methodLabels = {
    cash: 'Efectivo',
    transfer: 'Transferencia',
    card: 'Tarjeta',
    check: 'Cheque',
    mercadopago: 'MercadoPago',
  };
  
  // Draw horizontal line below the header
  doc.line(10, startY + clientHeight + 10, doc.internal.pageSize.width - 10, startY + clientHeight + 10);
  
  // Payment information
  const paymentY = startY + clientHeight + 20;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('RECIBIMOS LA SUMA DE:', 10, paymentY);
  
  // Amount in numbers
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(payment.amount), 10, paymentY + 10);
  if (payment.currency === 'USD' && (payment.exchange_rate && payment.exchange_rate !== 1)) {
    const rate = payment.exchange_rate || 0;
    doc.setFontSize(9);
    doc.text('Moneda: Dólar Estadounidense (USD)', 60, paymentY + 10);
    doc.text(`TC: ${rate.toFixed ? rate.toFixed(3) : rate} ARS/USD`, 60, paymentY + 15);
    if (payment.amount_ars) {
      doc.text(`Monto ARS: ${formatCurrency(payment.amount_ars)}`, 60, paymentY + 20);
    }
    doc.setFontSize(10);
  }
  
  // Amount in words
  doc.setFont('helvetica', 'bold');
  if (payment.currency === 'USD') {
    doc.text('SON DÓLARES:', 10, paymentY + 25);
  } else {
    doc.text('SON PESOS:', 10, paymentY + 25);
  }
  doc.setFont('helvetica', 'normal');
  
  // Split long text into multiple lines if needed
  const amountInWords = numeroALetras(Math.floor(payment.amount)).toUpperCase();
  const maxWidth = doc.internal.pageSize.width - 20; // 10px margin on each side
  const lines = doc.splitTextToSize(amountInWords, maxWidth);
  doc.text(lines, 10, paymentY + 35);
  
  // Calculate next Y position based on number of lines
  const nextY = paymentY + 35 + (lines.length * 5);
  
  // Payment method
  doc.setFont('helvetica', 'bold');
  doc.text('FORMA DE PAGO:', 10, nextY + 10);
  doc.setFont('helvetica', 'normal');
  doc.text(methodLabels[payment.method], 10, nextY + 20);
  
  // Reference if available
  let totalY = nextY + 20;
  if (payment.reference) {
    doc.setFont('helvetica', 'bold');
    doc.text('REFERENCIA:', 10, totalY + 10);
    doc.setFont('helvetica', 'normal');
    doc.text(payment.reference, 10, totalY + 20);
    totalY += 20;
  }
  
  // Total
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL COBRADO:', 10, totalY + 10);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(payment.amount), 10, totalY + 20);
  
  // Save the PDF
  doc.save(`recibo-${payment.receipt_number || payment.id.substring(0, 8)}.pdf`);
};

export const generateDeliveryNotePDF = async (deliveryNote: any) => {
  const doc = new jsPDF();
  
  // Set font
  doc.setFont('helvetica');
  
  // Company header - Left side
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_NAME, 10, 20);
  
  // Document type below company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('REMITO', 10, 30);
  
  // Company details - Right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, doc.internal.pageSize.width - 10, 15, { align: 'right' });
  doc.text(COMPANY_ADDRESS, doc.internal.pageSize.width - 10, 20, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, doc.internal.pageSize.width - 10, 25, { align: 'right' });
  doc.text(`e-mail: ${COMPANY_EMAIL}`, doc.internal.pageSize.width - 10, 30, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, doc.internal.pageSize.width - 10, 35, { align: 'right' });
  
  // Document validity notice - Center
  doc.setFontSize(10);
  doc.setFont('helvetica');
  doc.text('DOCUMENTO NO VÁLIDO COMO FACTURA', doc.internal.pageSize.width / 2, 45, { align: 'center' });
  
  // Draw horizontal line
  doc.line(10, 55, doc.internal.pageSize.width - 10, 55);
  
  // Client and Delivery Note details in a table-like structure
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Create a table with two columns
  const startY = 65;
  
  // Draw rectangle for client data
  doc.rect(10, startY, doc.internal.pageSize.width / 2 - 15, 40);
  
  // Draw rectangle for delivery note data
  doc.rect(doc.internal.pageSize.width / 2 - 5, startY, doc.internal.pageSize.width / 2 - 5, 40);
  
  // Left column - Client data
  doc.text('Datos del Cliente', 15, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  let yPos = startY + 20;
  
  // Nombre del cliente
  if (deliveryNote.client_name) {
    doc.text(`Nombre: ${deliveryNote.client_name}`, 15, yPos);
    yPos += 5;
  }
  
  // Dirección
  if (deliveryNote.client_address) {
    doc.text(`Dirección: ${deliveryNote.client_address}`, 15, yPos);
    yPos += 5;
  }
  
  // Ciudad y Provincia
  if (deliveryNote.client_city || deliveryNote.client_province) {
    const location = [
      deliveryNote.client_city,
      deliveryNote.client_province
    ].filter(Boolean).join(', ');
    doc.text(`Ciudad/Provincia: ${location}`, 15, yPos);
    yPos += 5;
  }
  
  // CUIT
  if (deliveryNote.client_cuit) {
    doc.text(`CUIT: ${deliveryNote.client_cuit}`, 15, yPos);
    yPos += 5;
  }
  
  // Right column - Delivery Note data
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Remito', doc.internal.pageSize.width / 2, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nro.: ${deliveryNote.number}`, doc.internal.pageSize.width / 2, startY + 20);
  doc.text(`Fecha: ${formatArgentinaDate(deliveryNote.date)}`, doc.internal.pageSize.width / 2, startY + 30);
  
  // Carrier information
  const carrierY = startY + 50;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Datos del Transportista', 10, carrierY);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Transportista: ${deliveryNote.carrier_name || 'No especificado'}`, 10, carrierY + 10);
  
  // Items table
  doc.autoTable({
    startY: carrierY + 20,
    head: [['DESCRIPCIÓN', 'CANTIDAD']],
    body: deliveryNote.items.map((item: any) => [
      item.description,
      item.quantity.toString(),
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 25, halign: 'right' },
    },
    theme: 'plain',
    margin: { left: 10, right: 10 },
  });
  
  // Firmas (dos renglones: entregado por arriba, recibido en conformidad abajo)
  const sigLeft = 30;
  const sigRight = doc.internal.pageSize.width - 30;
  const sigStartY = (doc as any).lastAutoTable.finalY + 30;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const deliveredLineY = sigStartY;
  doc.line(sigLeft, deliveredLineY, sigRight, deliveredLineY);
  doc.text(
    `Entregado por: ${deliveryNote.delivered_by || '___________________'}`,
    sigLeft,
    deliveredLineY + 8
  );

  const receivedLineY = deliveredLineY + 28;
  doc.line(sigLeft, receivedLineY, sigRight, receivedLineY);
  doc.text('Recibido en conformidad por: _____________________', sigLeft, receivedLineY + 8);

  // Legal warning footer
  const footerY = receivedLineY + 28;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  const legalText = [
    'La firma del presente constituye plena prueba de conformidad y recepción satisfactoria de la',
    'mercaderia detallada en cuanto a calidad y cantidad conforme a lo pactado.',
  ];
  
  let currentY = footerY;
  legalText.forEach((line, index) => {
    doc.text(line, doc.internal.pageSize.width / 2, currentY + (index * 4), { align: 'center' });
  });
  
  // Save the PDF
  doc.save(`remito-${deliveryNote.number}.pdf`);
};

// Helper function to get invoice details
async function getInvoiceDetails(invoiceId: string) {
  try {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching invoice details:', error);
    return null;
  }
}

// Helper function to get client details
async function getClientDetails(clientId: string) {
  try {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single();
    
    if (error) {
      console.error('Error fetching client details:', error);
      return null;
    }
    
    console.log('Client data fetched:', data); // Para debug
    return data;
  } catch (error) {
    console.error('Error fetching client details:', error);
    return null;
  }
}

// Nueva función para generar PDF de ProductionAdmin mejorado
export const generateProductionAdminPDF = async (
  kpis: {
    averageProfitability: number;
    totalCost: number;
    totalValue: number;
    pendingTasksCount: number;
    inProgressTasksCount: number;
    finishedTasksCount: number;
  },
  filteredProductions: any[],
  filteredTasks: any[],
  taskTypeChartData: any,
  supplierChartData: any,
  clients: any[],
  suppliers: any[],
  taskTypes: any[],
  filters: any
) => {
  // Crear PDF en formato horizontal con mejor configuración
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
    compress: true
  });
  
  // Configuración inicial mejorada
  doc.setFont('helvetica');
  
  // Obtener dimensiones de la página
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  // const contentHeight = pageHeight - (margin * 2); // No se usa actualmente
  
  // Función para verificar si hay espacio suficiente en la página
  const checkPageSpace = (requiredHeight: number, currentY: number) => {
    return (currentY + requiredHeight) <= (pageHeight - margin);
  };
  
  // Función para agregar nueva página si es necesario
  const addPageIfNeeded = (requiredHeight: number, currentY: number) => {
    if (!checkPageSpace(requiredHeight, currentY)) {
      doc.addPage('landscape');
      return margin + 10; // Retorna nueva posición Y
    }
    return currentY;
  };
  
  // Función helper mejorada para manejar saltos de línea
  const drawWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 10, color: number[] = [0, 0, 0]) => {
    doc.setFontSize(fontSize);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line: string, index: number) => {
      doc.text(line, x, y + (index * fontSize * 0.4));
    });
    return lines.length * fontSize * 0.4; // Retorna la altura total del texto
  };
  
  // Función para crear tarjetas con sombra y bordes redondeados
  const drawCard = (x: number, y: number, width: number, height: number, fillColor: number[], _textColor: number[] = [255, 255, 255], _radius: number = 3) => {
    // Sombra
    doc.setFillColor(0, 0, 0, 0.1);
    doc.rect(x + 2, y + 2, width, height, 'F');
    
    // Tarjeta principal
    doc.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
    doc.rect(x, y, width, height, 'F');
    
    // Borde
    doc.setDrawColor(fillColor[0] * 0.8, fillColor[1] * 0.8, fillColor[2] * 0.8);
    doc.setLineWidth(0.5);
    doc.rect(x, y, width, height);
    
    return { x, y, width, height };
  };
  
  // Función para crear gráficos con mejor calidad
  const drawBarChart = (data: any[], x: number, y: number, width: number, height: number, title: string) => {
    const chartY = addPageIfNeeded(height + 40, y);
    
    // Título del gráfico
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(title, x, chartY);
    
    if (!data || data.length === 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('No hay datos disponibles', x, chartY + 20);
      return chartY + 30;
    }
    
    const chartStartY = chartY + 15;
    const chartHeight = height - 30;
    const chartWidth = width - 40;
    const barWidth = Math.min(chartWidth / data.length, 40);
    const maxValue = Math.max(...data.map(d => d.value));
    
    // Dibujar ejes con mejor estilo
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(1);
    doc.line(x + 20, chartStartY + chartHeight, x + 20, chartStartY);
    doc.line(x + 20, chartStartY + chartHeight, x + 20 + chartWidth, chartStartY + chartHeight);
    
    // Dibujar barras con gradiente simulado
    data.forEach((item, index) => {
      const barHeight = maxValue > 0 ? (item.value / maxValue) * chartHeight : 0;
      const barX = x + 20 + (index * barWidth) + (barWidth * 0.1);
      const barY = chartStartY + chartHeight - barHeight;
      const barWidthFinal = barWidth * 0.8;
      
      if (barX + barWidthFinal <= x + 20 + chartWidth) {
        // Color de la barra
        const colors = [
          [75, 192, 192], [153, 102, 255], [255, 159, 64], [255, 99, 132],
          [54, 162, 235], [255, 206, 86], [231, 233, 237], [255, 159, 64]
        ];
        const color = colors[index % colors.length];
        
        // Dibujar barra con efecto 3D
        doc.setFillColor(color[0], color[1], color[2]);
        doc.rect(barX, barY, barWidthFinal, barHeight, 'F');
        
        // Sombra de la barra
        doc.setFillColor(color[0] * 0.7, color[1] * 0.7, color[2] * 0.7);
        doc.rect(barX + 1, barY + 1, barWidthFinal, barHeight, 'F');
        
        // Barra principal
        doc.setFillColor(color[0], color[1], color[2]);
        doc.rect(barX, barY, barWidthFinal, barHeight, 'F');
        
        // Etiqueta del valor
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(formatCurrency(item.value), barX + barWidthFinal/2, barY - 5, { align: 'center' });
        
        // Etiqueta del eje X
        doc.setFontSize(8);
        const labelY = chartStartY + chartHeight + 15;
        const truncatedLabel = item.label.length > 12 ? item.label.substring(0, 9) + '...' : item.label;
        doc.text(truncatedLabel, barX + barWidthFinal/2, labelY, { align: 'center' });
      }
    });
    
    // Etiquetas de los ejes
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Valor ($)', x + 10, chartStartY + chartHeight/2, { align: 'center', angle: 90 });
    doc.text('Categorías', x + 20 + chartWidth/2, chartStartY + chartHeight + 30, { align: 'center' });
    
    return chartY + height;
  };
  
  // Función helper para obtener el período del reporte
  const getPeriodText = () => {
    if (filters.customDateRange && filters.startDate && filters.endDate) {
      return `Desde ${filters.startDate} hasta ${filters.endDate}`;
    } else {
      const periodMap: Record<string, string> = {
        'all': 'Todo el período',
        '7days': 'Últimos 7 días',
        '30days': 'Últimos 30 días',
        'month': 'Este mes',
        'lastMonth': 'Mes anterior',
        'quarter': 'Este trimestre',
        'year': 'Este año'
      };
      return periodMap[filters.period] || 'Período no especificado';
    }
  };
  
  // ===== PÁGINA 1: ENCABEZADO MEJORADO Y RESUMEN EJECUTIVO =====
  
  // Encabezado de la empresa sin fondo
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(COMPANY_NAME, margin, 25);
  
  // Título del reporte con período
  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.text('REPORTE DE PRODUCCIÓN', margin, 35);
  
  // Período del reporte
  doc.setFontSize(12);
  doc.text(`Período: ${getPeriodText()}`, margin, 45);
  
  // Información de la empresa en la esquina derecha con mejor formato
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, pageWidth - margin, 20, { align: 'right' });
  doc.text(COMPANY_ADDRESS, pageWidth - margin, 25, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, pageWidth - margin, 30, { align: 'right' });
  doc.text(`E-mail: ${COMPANY_EMAIL}`, pageWidth - margin, 35, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, pageWidth - margin, 40, { align: 'right' });
  
  // Fecha de generación
  doc.setFontSize(8);
  doc.text(`Generado el: ${new Date().toLocaleDateString('es-AR')}`, pageWidth - margin, 50, { align: 'right' });
  
  // Línea separadora con mejor estilo
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(1);
  doc.line(margin, 60, pageWidth - margin, 60);
  
  // Información del período y filtros en dos columnas
  const leftColumn = margin;
  const rightColumn = pageWidth / 2 + 10;
  const columnWidth = (pageWidth - margin * 3) / 2;
  
  // Columna izquierda - Período
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Período del Reporte:', leftColumn, 75);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  drawWrappedText(getPeriodText(), leftColumn, 85, columnWidth);
  
  // Columna derecha - Filtros aplicados
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Filtros Aplicados:', rightColumn, 75);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  let yPos = 85;
  if (filters.clientId !== 'all') {
    const client = clients.find(c => c.id === filters.clientId);
    const filterText = `Cliente: ${client?.name || 'No encontrado'}`;
    drawWrappedText(filterText, rightColumn, yPos, columnWidth);
    yPos += 12;
  }
  
  if (filters.productionId !== 'all') {
    const production = filteredProductions.find(p => p.id === filters.productionId);
    const filterText = `Producción: ${production?.name || 'No encontrada'}`;
    drawWrappedText(filterText, rightColumn, yPos, columnWidth);
    yPos += 12;
  }
  
  if (filters.taskType !== 'all') {
    const taskType = taskTypes.find(t => t.id === filters.taskType);
    const filterText = `Tipo de Tarea: ${taskType?.name || 'No encontrado'}`;
    drawWrappedText(filterText, rightColumn, yPos, columnWidth);
    yPos += 12;
  }
  
  if (filters.supplierId !== 'all') {
    const supplier = suppliers.find(s => s.id === filters.supplierId);
    const filterText = `Proveedor: ${supplier?.name || 'No encontrado'}`;
    drawWrappedText(filterText, rightColumn, yPos, columnWidth);
    yPos += 12;
  }
  
  if (filters.taskStatus !== 'all') {
    const statusMap: Record<string, string> = {
      'pending': 'Pendiente',
      'in_progress': 'En Proceso',
      'finished': 'Terminado'
    };
    const filterText = `Estado: ${statusMap[filters.taskStatus] || filters.taskStatus}`;
    drawWrappedText(filterText, rightColumn, yPos, columnWidth);
    yPos += 12;
  }
  
  // KPIs en formato de tarjetas mejoradas con sombras - centrados y más arriba
  const kpiStartY = Math.max(80, yPos + 10);
  const kpiWidth = (contentWidth - 30) / 4;
  const kpiHeight = 40;
  
  // Centrar los KPIs
  const kpiTotalWidth = (kpiWidth * 4) + (5 * 3); // 4 tarjetas + 3 espacios de 5
  const kpiStartX = (pageWidth - kpiTotalWidth) / 2;
  
  // KPI 1: Rentabilidad
  drawCard(kpiStartX, kpiStartY, kpiWidth, kpiHeight, [41, 128, 185]);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('RENTABILIDAD', kpiStartX + kpiWidth/2, kpiStartY + 12, { align: 'center' });
  doc.setFontSize(16);
  doc.text(`${kpis.averageProfitability.toFixed(2)}%`, kpiStartX + kpiWidth/2, kpiStartY + 28, { align: 'center' });
  
  // KPI 2: Costo Total
  drawCard(kpiStartX + 5 + kpiWidth, kpiStartY, kpiWidth, kpiHeight, [231, 76, 60]);
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('COSTO TOTAL', kpiStartX + 5 + kpiWidth + kpiWidth/2, kpiStartY + 12, { align: 'center' });
  doc.setFontSize(12);
  doc.text(formatCurrency(kpis.totalCost), kpiStartX + 5 + kpiWidth + kpiWidth/2, kpiStartY + 28, { align: 'center' });
  
  // KPI 3: Valor Total
  drawCard(kpiStartX + 10 + kpiWidth * 2, kpiStartY, kpiWidth, kpiHeight, [46, 204, 113]);
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('VALOR TOTAL', kpiStartX + 10 + kpiWidth * 2 + kpiWidth/2, kpiStartY + 12, { align: 'center' });
  doc.setFontSize(12);
  doc.text(formatCurrency(kpis.totalValue), kpiStartX + 10 + kpiWidth * 2 + kpiWidth/2, kpiStartY + 28, { align: 'center' });
  
  // KPI 4: Total de Tareas
  drawCard(kpiStartX + 15 + kpiWidth * 3, kpiStartY, kpiWidth, kpiHeight, [155, 89, 182]);
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL TAREAS', kpiStartX + 15 + kpiWidth * 3 + kpiWidth/2, kpiStartY + 12, { align: 'center' });
  doc.setFontSize(16);
  doc.text((kpis.pendingTasksCount + kpis.inProgressTasksCount + kpis.finishedTasksCount).toString(), kpiStartX + 15 + kpiWidth * 3 + kpiWidth/2, kpiStartY + 28, { align: 'center' });
  
  // Resetear color de texto
  doc.setTextColor(0, 0, 0);
  
  // Estado de tareas en formato de tarjetas mejoradas - centrados y más arriba
  const taskStatusY = kpiStartY + 50;
  const statusWidth = (contentWidth - 20) / 3;
  const statusHeight = 35;
  
  // Centrar las tarjetas de estado
  const statusTotalWidth = (statusWidth * 3) + (5 * 2); // 3 tarjetas + 2 espacios de 5
  const statusStartX = (pageWidth - statusTotalWidth) / 2;
  
  // Tareas Pendientes
  drawCard(statusStartX, taskStatusY, statusWidth, statusHeight, [255, 193, 7], [0, 0, 0]);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('PENDIENTES', statusStartX + statusWidth/2, taskStatusY + 12, { align: 'center' });
  doc.setFontSize(16);
  doc.text(kpis.pendingTasksCount.toString(), statusStartX + statusWidth/2, taskStatusY + 26, { align: 'center' });
  
  // Tareas En Proceso
  drawCard(statusStartX + 5 + statusWidth, taskStatusY, statusWidth, statusHeight, [52, 152, 219]);
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('EN PROCESO', statusStartX + 5 + statusWidth + statusWidth/2, taskStatusY + 12, { align: 'center' });
  doc.setFontSize(16);
  doc.text(kpis.inProgressTasksCount.toString(), statusStartX + 5 + statusWidth + statusWidth/2, taskStatusY + 26, { align: 'center' });
  
  // Tareas Terminadas
  drawCard(statusStartX + 10 + statusWidth * 2, taskStatusY, statusWidth, statusHeight, [46, 204, 113]);
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('TERMINADAS', statusStartX + 10 + statusWidth * 2 + statusWidth/2, taskStatusY + 12, { align: 'center' });
  doc.setFontSize(16);
  doc.text(kpis.finishedTasksCount.toString(), statusStartX + 10 + statusWidth * 2 + statusWidth/2, taskStatusY + 26, { align: 'center' });
  
  // ===== PÁGINA 2: GRÁFICO DE BARRAS MEJORADO =====
  doc.addPage('landscape');
  
  // Preparar datos para el gráfico de barras
  const chartData = taskTypeChartData && taskTypeChartData.labels.length > 0 
    ? taskTypeChartData.labels.map((label: string, index: number) => ({
        label: label.length > 15 ? label.substring(0, 12) + '...' : label,
        value: taskTypeChartData.datasets[0].data[index]
      }))
    : [];
  
  // Usar la función mejorada de gráfico de barras
  drawBarChart(chartData, margin, 25, contentWidth, 120, 'COSTO POR TIPO DE TAREA');
  
  // ===== PÁGINA 3: GRÁFICO DE TORTA MEJORADO =====
  doc.addPage('landscape');
  
  // Verificar espacio para el gráfico de torta
  const pieChartY = addPageIfNeeded(150, 25);
  
  // Gráfico de torta - Costo por Proveedor
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('COSTO POR PROVEEDOR', margin, pieChartY);
  
  if (supplierChartData && supplierChartData.labels.length > 0) {
    const pieCenterX = pageWidth / 2;
    const pieCenterY = pieChartY + 60;
    const pieRadius = 50;
    
    const total = supplierChartData.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
    let currentAngle = 0;
    
    // Colores del gráfico mejorados
    const colors = [
      [255, 99, 132], [54, 162, 235], [255, 206, 86], [75, 192, 192], [153, 102, 255],
      [255, 159, 64], [138, 201, 38], [249, 65, 68], [67, 170, 139], [39, 125, 161],
      [241, 91, 181], [155, 93, 229], [0, 187, 249], [0, 245, 212], [254, 228, 64]
    ];
    
    supplierChartData.labels.forEach((label: string, index: number) => {
      const value = supplierChartData.datasets[0].data[index];
      const sliceAngle = (value / total) * 360;
      
      const color = colors[index % colors.length];
      
      doc.setFillColor(color[0], color[1], color[2]);
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(1);
      
      // Dibujar sector del círculo con mejor calidad
      const startAngle = currentAngle;
      // const endAngle = currentAngle + sliceAngle; // No se usa actualmente
      
      // Dibujar sector completamente relleno
      const segments = Math.max(20, Math.floor(sliceAngle / 5));
      const angleStep = sliceAngle / segments;
      
      // Crear sector relleno con triángulos
      for (let i = 0; i < segments; i++) {
        const angle1 = startAngle + (i * angleStep);
        const angle2 = startAngle + ((i + 1) * angleStep);
        
        const x1 = pieCenterX + pieRadius * Math.cos((angle1 - 90) * Math.PI / 180);
        const y1 = pieCenterY + pieRadius * Math.sin((angle1 - 90) * Math.PI / 180);
        const x2 = pieCenterX + pieRadius * Math.cos((angle2 - 90) * Math.PI / 180);
        const y2 = pieCenterY + pieRadius * Math.sin((angle2 - 90) * Math.PI / 180);
        
        // Dibujar triángulo relleno
        doc.setFillColor(color[0], color[1], color[2]);
        doc.triangle(pieCenterX, pieCenterY, x1, y1, x2, y2, 'F');
      }
      
      // Etiqueta del proveedor con mejor posicionamiento
      const labelAngle = currentAngle + sliceAngle / 2;
      const labelRadius = pieRadius + 25;
      const labelX = pieCenterX + labelRadius * Math.cos((labelAngle - 90) * Math.PI / 180);
      const labelY = pieCenterY + labelRadius * Math.sin((labelAngle - 90) * Math.PI / 180);
      
      // Verificar que la etiqueta esté dentro de la página
      if (labelY > margin && labelY < pageHeight - margin) {
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        const truncatedLabel = label.length > 12 ? label.substring(0, 9) + '...' : label;
        doc.text(truncatedLabel, labelX, labelY, { align: 'center' });
        doc.text(formatCurrency(value), labelX, labelY + 10, { align: 'center' });
      }
      
      currentAngle += sliceAngle;
    });
    
    // Círculo central mejorado
    doc.setFillColor(255, 255, 255);
    doc.circle(pieCenterX, pieCenterY, 15, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(1);
    doc.circle(pieCenterX, pieCenterY, 15);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('TOTAL', pieCenterX, pieCenterY - 5, { align: 'center' });
    doc.setFontSize(10);
    doc.text(formatCurrency(total), pieCenterX, pieCenterY + 8, { align: 'center' });
  } else {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('No hay datos de proveedores disponibles', margin, pieChartY + 40);
  }
  
  // ===== PÁGINA 4: GRÁFICO DE LÍNEAS =====
  doc.addPage('landscape');
  
  // Gráfico de líneas - Evolución de costos por mes (simulado)
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('EVOLUCIÓN DE COSTOS MENSUALES', margin, 25);
  
  // Simular datos mensuales
  const monthlyData = [
    { month: 'Ene', cost: kpis.totalCost * 0.8 },
    { month: 'Feb', cost: kpis.totalCost * 0.9 },
    { month: 'Mar', cost: kpis.totalCost * 1.1 },
    { month: 'Abr', cost: kpis.totalCost * 0.95 },
    { month: 'May', cost: kpis.totalCost * 1.05 },
    { month: 'Jun', cost: kpis.totalCost }
  ];
  
  const lineChartStartY = 50;
  const lineChartHeight = 120;
  const lineChartWidth = contentWidth - 40;
  const pointSpacing = lineChartWidth / (monthlyData.length - 1);
  const maxMonthlyCost = Math.max(...monthlyData.map(d => d.cost));
  
  // Dibujar ejes
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.5);
  doc.line(margin + 30, lineChartStartY + lineChartHeight, margin + 30, lineChartStartY);
  doc.line(margin + 30, lineChartStartY + lineChartHeight, margin + 30 + lineChartWidth, lineChartStartY + lineChartHeight);
  
  // Dibujar línea del gráfico
  doc.setDrawColor(75, 192, 192);
  doc.setLineWidth(3);
  
  monthlyData.forEach((data, index) => {
    const x = margin + 30 + (index * pointSpacing);
    const y = lineChartStartY + lineChartHeight - (data.cost / maxMonthlyCost) * lineChartHeight;
    
    // Dibujar punto
    doc.setFillColor(75, 192, 192);
    doc.circle(x, y, 5, 'F');
    
    // Conectar con el punto anterior
    if (index > 0) {
      const prevX = margin + 30 + ((index - 1) * pointSpacing);
      const prevY = lineChartStartY + lineChartHeight - (monthlyData[index - 1].cost / maxMonthlyCost) * lineChartHeight;
      doc.line(prevX, prevY, x, y);
    }
    
    // Etiqueta del mes
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(data.month, x, lineChartStartY + lineChartHeight + 20, { align: 'center' });
    
    // Etiqueta del valor
    doc.text(formatCurrency(data.cost), x, y - 15, { align: 'center' });
  });
  
  // Etiquetas de los ejes
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Costo ($)', margin + 15, lineChartStartY + lineChartHeight/2, { align: 'center', angle: 90 });
  doc.text('Meses', margin + 30 + lineChartWidth/2, lineChartStartY + lineChartHeight + 40, { align: 'center' });
  
  // ===== PÁGINA 5: GRÁFICO DE DONA =====
  doc.addPage('landscape');
  
  // Gráfico de dona - Distribución de estados de tareas
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('DISTRIBUCIÓN DE ESTADOS DE TAREAS', margin, 25);
  
  const totalTasks = kpis.pendingTasksCount + kpis.inProgressTasksCount + kpis.finishedTasksCount;
  if (totalTasks > 0) {
    const donutCenterX = pageWidth / 2;
    const donutCenterY = pageHeight / 2 - 20;
    const donutRadius = 60;
    const donutInnerRadius = 30;
    
    let currentAngle = 0;
    const taskData = [
      { count: kpis.pendingTasksCount, color: [255, 193, 7], label: 'Pendientes' },
      { count: kpis.inProgressTasksCount, color: [52, 152, 219], label: 'En Proceso' },
      { count: kpis.finishedTasksCount, color: [46, 204, 113], label: 'Terminadas' }
    ];
    
    taskData.forEach((task, index) => {
      if (task.count > 0) {
        const sliceAngle = (task.count / totalTasks) * 360;
        
        // Dibujar sector exterior
        doc.setFillColor(task.color[0], task.color[1], task.color[2]);
        doc.setDrawColor(task.color[0], task.color[1], task.color[2]);
        
        // Crear sector completamente relleno del donut
        const segments = Math.max(15, Math.floor(sliceAngle / 8));
        const angleStep = sliceAngle / segments;
        
        for (let i = 0; i < segments; i++) {
          const angle1 = currentAngle + (i * angleStep);
          const angle2 = currentAngle + ((i + 1) * angleStep);
          
          const x1 = donutCenterX + donutRadius * Math.cos((angle1 - 90) * Math.PI / 180);
          const y1 = donutCenterY + donutRadius * Math.sin((angle1 - 90) * Math.PI / 180);
          const x2 = donutCenterX + donutRadius * Math.cos((angle2 - 90) * Math.PI / 180);
          const y2 = donutCenterY + donutRadius * Math.sin((angle2 - 90) * Math.PI / 180);
          
          const innerX1 = donutCenterX + donutInnerRadius * Math.cos((angle1 - 90) * Math.PI / 180);
          const innerY1 = donutCenterY + donutInnerRadius * Math.sin((angle1 - 90) * Math.PI / 180);
          const innerX2 = donutCenterX + donutInnerRadius * Math.cos((angle2 - 90) * Math.PI / 180);
          const innerY2 = donutCenterY + donutInnerRadius * Math.sin((angle2 - 90) * Math.PI / 180);
          
          // Dibujar sector relleno del donut con triángulos
          doc.setFillColor(task.color[0], task.color[1], task.color[2]);
          doc.triangle(x1, y1, x2, y2, innerX1, innerY1, 'F');
          doc.triangle(x2, y2, innerX1, innerY1, innerX2, innerY2, 'F');
        }
        
        // Etiqueta del sector
        const labelAngle = currentAngle + sliceAngle / 2;
        const labelRadius = donutRadius + 20;
        const labelX = donutCenterX + labelRadius * Math.cos((labelAngle - 90) * Math.PI / 180);
        const labelY = donutCenterY + labelRadius * Math.sin((labelAngle - 90) * Math.PI / 180);
        
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(task.label, labelX, labelY, { align: 'center' });
        doc.text(`${task.count}`, labelX, labelY + 12, { align: 'center' });
        
        currentAngle += sliceAngle;
      }
    });
    
    // Círculo central
    doc.setFillColor(255, 255, 255);
    doc.circle(donutCenterX, donutCenterY, donutInnerRadius, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(1);
    doc.circle(donutCenterX, donutCenterY, donutInnerRadius);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('TOTAL', donutCenterX, donutCenterY - 3, { align: 'center' });
    doc.setFontSize(10);
    doc.text(totalTasks.toString(), donutCenterX, donutCenterY + 8, { align: 'center' });
  }
  
  // ===== PÁGINA 6: RANKINGS Y ANÁLISIS =====
  doc.addPage('landscape');
  
  // Ranking de rentabilidad por producción
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('RANKING DE RENTABILIDAD POR PRODUCCIÓN', margin, 25);
  
  if (filteredProductions.length > 0) {
    // Preparar datos para el ranking
    const productionRanking = filteredProductions.map((production) => {
      const productionTasks = filteredTasks.filter(task => task.production_id === production.id);
      const totalCost = productionTasks.reduce((sum, task) => sum + task.cost, 0);
      const profitability = production.production_value > 0 
        ? ((production.production_value - totalCost) / production.production_value) * 100 
        : 0;
      
      const client = clients.find(c => c.id === production.client_id);
      
      return {
        name: production.name,
        client: client?.name || 'Cliente no encontrado',
        value: production.production_value,
        cost: totalCost,
        profitability: profitability,
        tasks: productionTasks.length
      };
    }).sort((a, b) => b.profitability - a.profitability); // Ordenar por rentabilidad descendente
    
    const rankingTableData = productionRanking.map((prod, index) => [
      `#${index + 1}`,
      prod.name.length > 25 ? prod.name.substring(0, 22) + '...' : prod.name,
      (prod.client).length > 20 ? prod.client.substring(0, 17) + '...' : prod.client,
      formatCurrency(prod.value),
      formatCurrency(prod.cost),
      `${prod.profitability.toFixed(2)}%`,
      prod.tasks.toString()
    ]);
    
    doc.autoTable({
      startY: 35,
      head: [['Rank', 'Producción', 'Cliente', 'Valor', 'Costo', 'Rentabilidad', 'Tareas']],
      body: rankingTableData,
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      theme: 'grid',
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' },
        6: { cellWidth: 25, halign: 'center' },
      },
    });
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('No hay producciones que coincidan con los filtros', margin, 35);
  }
  
  // Ranking de rentabilidad por cliente
  const clientRankingY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 30 : 80;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('RANKING DE RENTABILIDAD POR CLIENTE', margin, clientRankingY);
  
  // Agrupar producciones por cliente
  const clientAnalysis = new Map();
  filteredProductions.forEach(production => {
    const client = clients.find(c => c.id === production.client_id);
    if (client) {
      if (!clientAnalysis.has(client.id)) {
        clientAnalysis.set(client.id, {
          name: client.name,
          productions: [],
          totalValue: 0,
          totalCost: 0
        });
      }
      
      const clientData = clientAnalysis.get(client.id);
      const productionTasks = filteredTasks.filter(task => task.production_id === production.id);
      const totalCost = productionTasks.reduce((sum, task) => sum + task.cost, 0);
      
      clientData.productions.push(production);
      clientData.totalValue += production.production_value;
      clientData.totalCost += totalCost;
    }
  });
  
  // Crear ranking de clientes
  const clientRanking = Array.from(clientAnalysis.values()).map(clientData => {
    const profitability = clientData.totalValue > 0 
      ? ((clientData.totalValue - clientData.totalCost) / clientData.totalValue) * 100 
      : 0;
    
    return {
      name: clientData.name,
      productions: clientData.productions.length,
      totalValue: clientData.totalValue,
      totalCost: clientData.totalCost,
      profitability: profitability
    };
  }).sort((a, b) => b.profitability - a.profitability); // Ordenar por rentabilidad descendente
  
  if (clientRanking.length > 0) {
    const clientRankingTableData = clientRanking.map((client, index) => [
      `#${index + 1}`,
      client.name.length > 30 ? client.name.substring(0, 27) + '...' : client.name,
      client.productions.toString(),
      formatCurrency(client.totalValue),
      formatCurrency(client.totalCost),
      `${client.profitability.toFixed(2)}%`
    ]);
    
    doc.autoTable({
      startY: clientRankingY + 15,
      head: [['Rank', 'Cliente', 'Producciones', 'Valor Total', 'Costo Total', 'Rentabilidad']],
      body: clientRankingTableData,
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [75, 192, 192],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      theme: 'grid',
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' },
      },
    });
  }
  
  // ===== PÁGINA 7: RESUMEN DETALLADO =====
  doc.addPage('landscape');
  
  // Resumen de producciones
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('RESUMEN DETALLADO DE PRODUCCIONES', margin, 25);
  
  if (filteredProductions.length > 0) {
    const productionTableData = filteredProductions.map((production) => {
      const productionTasks = filteredTasks.filter(task => task.production_id === production.id);
      const totalCost = productionTasks.reduce((sum, task) => sum + task.cost, 0);
      const profitability = production.production_value > 0 
        ? ((production.production_value - totalCost) / production.production_value) * 100 
        : 0;
      
      const client = clients.find(c => c.id === production.client_id);
      
      return [
        production.name.length > 30 ? production.name.substring(0, 27) + '...' : production.name,
        (client?.name || 'Cliente no encontrado').length > 25 ? (client?.name || 'Cliente no encontrado').substring(0, 22) + '...' : (client?.name || 'Cliente no encontrado'),
        formatCurrency(production.production_value),
        formatCurrency(totalCost),
        `${profitability.toFixed(2)}%`,
        productionTasks.length.toString()
      ];
    });
    
    doc.autoTable({
      startY: 35,
      head: [['Producción', 'Cliente', 'Valor', 'Costo', 'Rentabilidad', 'Tareas']],
      body: productionTableData,
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      theme: 'grid',
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 25, halign: 'center' },
      },
    });
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('No hay producciones que coincidan con los filtros', margin, 35);
  }
  
  // Análisis de rentabilidad por cliente
  const analysisY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 30 : 80;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('ANÁLISIS DE RENTABILIDAD POR CLIENTE', margin, analysisY);
  
  if (clientAnalysis.size > 0) {
    const clientTableData = Array.from(clientAnalysis.values()).map(clientData => {
      const profitability = clientData.totalValue > 0 
        ? ((clientData.totalValue - clientData.totalCost) / clientData.totalValue) * 100 
        : 0;
      
      return [
        clientData.name.length > 30 ? clientData.name.substring(0, 27) + '...' : clientData.name,
        clientData.productions.length.toString(),
        formatCurrency(clientData.totalValue),
        formatCurrency(clientData.totalCost),
        `${profitability.toFixed(2)}%`
      ];
    });
    
    doc.autoTable({
      startY: analysisY + 15,
      head: [['Cliente', 'Producciones', 'Valor Total', 'Costo Total', 'Rentabilidad']],
      body: clientTableData,
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [75, 192, 192],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      theme: 'grid',
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' },
      },
    });
  }
  
  // Pie de página con información del reporte
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Pie de página removido - se maneja al final del documento
  }
  
  // Agregar pie de página a todas las páginas
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Pie de página con información de la empresa - posicionamiento mejorado
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    
    // Línea separadora sutil
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    
    // Información de la empresa (izquierda)
    doc.text(`${COMPANY_NAME} - Reporte de Producción`, margin, pageHeight - 8);
    
    // Fecha de generación (centro)
    doc.text(`Generado el ${new Date().toLocaleDateString('es-AR')}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    
    // Número de página (derecha)
    doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }
  
  // Guardar el PDF con nombre mejorado
  const fileName = `reporte-produccion-${new Date().toISOString().split('T')[0]}-${new Date().toTimeString().split(' ')[0].replace(/:/g, '')}.pdf`;
  doc.save(fileName);
};

// Helper function to get budget items
const getBudgetItems = async (budgetId: string): Promise<BudgetItem[]> => {
  try {
    console.log('Fetching budget items for budget ID:', budgetId);
    const { data, error } = await supabase
      .from('budget_items')
      .select('*')
      .eq('budget_id', budgetId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching budget items:', error);
      throw error;
    }
    
    console.log('Budget items query result:', data);
    return data || [];
  } catch (error) {
    console.error('Error fetching budget items:', error);
    return [];
  }
};
